import { useRef, useState, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, useTexture, Html } from "@react-three/drei";
import * as THREE from "three";
import { ArrowRight } from "lucide-react";
import { Eyebrow, SerifH } from "./shared";
import { HOTSPOTS_3D } from "../data/content";

function JellabaMesh({ onHotspotClick, active }) {
  const meshRef = useRef();
  const texture = useTexture("/jellaba_3d_cutout.png");
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.35) * 0.15;
    }
  });
  const w = 2.2;
  const h = 4.0;
  return (
    <group>
      <mesh ref={meshRef}>
        <planeGeometry args={[w, h, 32, 32]} />
        <meshStandardMaterial
          map={texture}
          transparent
          alphaTest={0.05}
          side={THREE.DoubleSide}
          metalness={0.05}
          roughness={0.85}
        />
        {/* brass dot markers only — popup rendered outside the Canvas */}
        {HOTSPOTS_3D.map((hs) => (
          <Html
            key={hs.id}
            position={hs.position}
            center
            distanceFactor={6}
            zIndexRange={[10, 0]}
            style={{ pointerEvents: "auto" }}
          >
            <button
              data-testid={`hotspot-${hs.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onHotspotClick(hs.id);
              }}
              aria-label={hs.title}
              className={`h-4 w-4 rounded-full ring-4 transition-transform ${
                active === hs.id
                  ? "bg-[#F4EDE2] ring-[#F4EDE2]/40 scale-125"
                  : "bg-[#C8A96A] ring-[#C8A96A]/30 hover:scale-125"
              }`}
            />
          </Html>
        ))}
      </mesh>
    </group>
  );
}

export function ThreeDShowcase() {
  const [active, setActive] = useState(null);
  return (
    <section
      id="craftsmanship-3d"
      data-testid="three-d-showcase"
      className="relative overflow-hidden bg-[#0C111C] text-[#F4EDE2]"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side at 30% 50%, rgba(200,169,106,0.14), transparent 60%)",
        }}
      />
      <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20 py-24 md:py-36 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* Left: real 3D canvas */}
        <div className="relative" data-testid="three-d-stage">
          <div className="relative w-full aspect-[3/4] rounded-md overflow-hidden">
            <Canvas
              camera={{ position: [0, 0, 5.2], fov: 42 }}
              gl={{ antialias: true, alpha: true }}
              dpr={[1, 2]}
            >
              <color attach="background" args={["#0C111C"]} />
              <ambientLight intensity={0.55} />
              <directionalLight position={[3, 4, 5]} intensity={1.1} color="#F4EDE2" />
              <directionalLight position={[-4, 2, 3]} intensity={0.6} color="#C8A96A" />
              <Suspense fallback={null}>
                <JellabaMesh
                  onHotspotClick={(id) => setActive(id === active ? null : id)}
                  active={active}
                />
              </Suspense>
              <OrbitControls
                enableZoom={false}
                enablePan={false}
                autoRotate={false}
                minPolarAngle={Math.PI * 0.35}
                maxPolarAngle={Math.PI * 0.65}
                rotateSpeed={0.8}
              />
            </Canvas>
            {/* Popup — absolutely positioned next to each hotspot */}
            <AnimatePresence>
              {active &&
                (() => {
                  const hs = HOTSPOTS_3D.find((x) => x.id === active);
                  return (
                    <motion.div
                      key={hs.id}
                      data-testid={`hotspot-card-${hs.id}`}
                      initial={{ opacity: 0, y: 6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 6, scale: 0.97 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute z-30 w-56 rounded-xl border border-[#C8A96A]/50 bg-[#0C111C]/92 backdrop-blur-md p-4 shadow-2xl text-[#F4EDE2] pointer-events-auto"
                      style={{ top: hs.card.top, left: hs.card.left }}
                    >
                      <p
                        className="text-[10px] tracking-[0.22em] uppercase text-[#C8A96A]"
                        style={{ fontFamily: "Inter, sans-serif" }}
                      >
                        Detail
                      </p>
                      <p
                        className="mt-1.5 text-base leading-tight"
                        style={{ fontFamily: "Fraunces, serif" }}
                      >
                        {hs.title}
                      </p>
                      <p
                        className="mt-1.5 text-[12px] leading-relaxed text-[#F4EDE2]/90"
                        style={{ fontFamily: "Inter, sans-serif" }}
                      >
                        {hs.detail}
                      </p>
                      <button
                        onClick={() => setActive(null)}
                        className="mt-2.5 text-[11px] text-[#F4EDE2]/60 hover:text-[#C8A96A]"
                        style={{ fontFamily: "Inter, sans-serif" }}
                      >
                        Close ×
                      </button>
                    </motion.div>
                  );
                })()}
            </AnimatePresence>
          </div>
          <p
            className="mt-8 text-center text-[11px] tracking-[0.24em] uppercase text-[#F4EDE2]/60"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            ⤺ Drag to orbit · Tap a brass dot
          </p>
        </div>

        {/* Right: copy */}
        <div>
          <Eyebrow>Craft in 3D</Eyebrow>
          <SerifH className="mt-4 text-[#F4EDE2]" size="clamp(32px, 4vw, 64px)">
            Look closer.
            <br /> Every stitch tells a story.
          </SerifH>
          <p
            className="mt-6 max-w-md text-base md:text-lg text-[#F4EDE2]/80"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Drag the jellaba to orbit in real 3D. Tap any brass hotspot to see the detail our atelier obsesses over — from the hand-stitched sfifa trim to the invisible deep pockets.
          </p>
          <a
            href="#craftsmanship"
            data-testid="threed-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#C8A96A] text-[#0C111C] px-7 py-3.5 font-medium hover:bg-[#F4EDE2] transition-colors"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Explore the fabric <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
